import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  Autocomplete,
  Tooltip,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SnoozeIcon from "@mui/icons-material/Snooze";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import API, { getErrorMessage } from "../api/axios";
import { useToast } from "../components/Toast";
import { ACCENT, NAVY } from "../theme";
import PageContainer from "../components/ui/PageContainer";
import { getUser } from "../auth/auth";
import LoadingState from "../components/ui/LoadingState";
import EmptyState from "../components/ui/EmptyState";
import StatusBadge from "../components/ui/StatusBadge";
import KpiCard from "../components/ui/KpiCard";
import RefreshButton from "../components/ui/RefreshButton";

const PRIORITY_TONE = { LOW: "neutral", MEDIUM: "warn", HIGH: "danger" };

const freshTaskForm = (prefill = {}) => ({
  title: "",
  description: "",
  priority: "MEDIUM",
  dueDate: "",
  candidateId: prefill.candidateId || null,
  jobCode: prefill.jobCode || "",
});

const freshInterviewForm = () => ({
  candidate: null,
  scheduledAt: "",
  durationMinutes: 30,
  round: "",
  interviewerName: "",
  meetingLink: "",
  location: "",
});

export default function MyDay() {
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [myDay, setMyDay] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState(freshTaskForm());
  const [savingTask, setSavingTask] = useState(false);

  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [interviewForm, setInterviewForm] = useState(freshInterviewForm());
  const [savingInterview, setSavingInterview] = useState(false);

  // ── Feature A (My Day active roles) ──────────────────────────────────────
  // Reuses the EXISTING /jobs endpoint (already called by JobRequirements.jsx,
  // Clients.jsx, Users.jsx) and the EXISTING established definition of an
  // "active" role (status not ON_HOLD/CLOSED, default ACTIVE — same
  // predicate already used in Dashboard.jsx/Clients.jsx) — no new endpoint,
  // no new job status concept invented. Scoped to the current recruiter via
  // assignedRecruiter, matching how every other count on this page is
  // already scoped to "my" data. Fails safely: a failed request just leaves
  // this at 0/empty rather than crashing the page or showing a wrong count.
  // Placed here, before any early return below, per React's rules of hooks.
  const [myJobs, setMyJobs] = useState([]);
  useEffect(() => {
    const me = getUser()?.name;
    API.get("/jobs")
      .then((r) => {
        const all = r.data || [];
        setMyJobs(
          me
            ? all.filter((j) => (j.assignedRecruiter || "").includes(me))
            : all,
        );
      })
      .catch(() => setMyJobs([]));
  }, []);

  // Real data: GET /api/my-day (new aggregation endpoint) + the EXISTING
  // GET /api/candidates (already used by Candidates.jsx/Pipeline.jsx) so
  // interview cards can show a candidate's name — Interview only stores
  // candidateId, so the name is resolved client-side from this same list
  // rather than changing the Interview API's response shape.
  const fetchAll = async () => {
    try {
      const [dayRes, candRes] = await Promise.all([
        API.get("/my-day"),
        API.get("/candidates"),
      ]);
      setMyDay(dayRes.data);
      setCandidates(candRes.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not load My Day"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const candidateById = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => map.set(c.id, c));
    return map;
  }, [candidates]);

  const candidateName = (id) => {
    const c = candidateById.get(id);
    return c ? `${c.firstName || ""} ${c.lastName || ""}`.trim() : `#${id}`;
  };

  // ── Task actions ──────────────────────────────────────────────────────
  const openTaskForm = (prefill) => {
    setTaskForm(freshTaskForm(prefill));
    setShowTaskForm(true);
  };

  const submitTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSavingTask(true);
    try {
      await API.post("/tasks", {
        title: taskForm.title.trim(),
        description: taskForm.description || null,
        priority: taskForm.priority,
        dueDate: taskForm.dueDate || null,
        candidateId: taskForm.candidateId || null,
        jobCode: taskForm.jobCode || null,
      });
      toast.success("Task created");
      setShowTaskForm(false);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not create task"));
    } finally {
      setSavingTask(false);
    }
  };

  const completeTask = async (task) => {
    setBusyId(`task-${task.id}`);
    try {
      await API.post(`/tasks/${task.id}/complete`);
      toast.success("Task completed");
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not complete task"));
    } finally {
      setBusyId(null);
    }
  };

  // ── Snooze — hides a task from due/overdue until the chosen time, without
  // deleting or altering it otherwise. `days` is a simple preset (1, 3, 7);
  // the target moment is computed from the browser's current wall-clock
  // time, kept at 9:00 AM on the target day (a reasonable "start of your
  // day" default) and sent as a plain local ISO string with no timezone
  // conversion — the backend parses it as-is (LocalDateTime.parse), the
  // same "naive local time" convention already used for dueDate elsewhere
  // on this page.
  const snoozeTask = async (task, days) => {
    setBusyId(`task-${task.id}`);
    try {
      const target = new Date();
      target.setDate(target.getDate() + days);
      target.setHours(9, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      const iso = `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}:00`;
      await API.post(`/tasks/${task.id}/snooze`, { snoozedUntil: iso });
      toast.success(`Snoozed for ${days} day${days !== 1 ? "s" : ""}`);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not snooze task"));
    } finally {
      setBusyId(null);
    }
  };

  // ── Interview actions ────────────────────────────────────────────────
  const submitInterview = async () => {
    if (!interviewForm.candidate) {
      toast.error("Select a candidate");
      return;
    }
    if (!interviewForm.scheduledAt) {
      toast.error("Date/time is required");
      return;
    }
    setSavingInterview(true);
    try {
      const res = await API.post("/interviews", {
        candidateId: interviewForm.candidate.id,
        scheduledAt: interviewForm.scheduledAt,
        durationMinutes: Number(interviewForm.durationMinutes) || null,
        round: interviewForm.round || null,
        interviewerName: interviewForm.interviewerName || null,
        meetingLink: interviewForm.meetingLink || null,
        location: interviewForm.location || null,
      });
      // The interview save itself succeeded whenever we get here (a save
      // failure throws and is handled in the catch block below, so no
      // email is ever implied for a failed save). invitationEmailSent /
      // invitationEmailNote are additive fields the backend now returns
      // alongside the existing interview fields — reported separately so
      // scheduling is never shown as failed just because the email step
      // didn't succeed.
      if (res?.data?.invitationEmailSent) {
        toast.success(
          "Interview scheduled and invitation email sent successfully.",
        );
      } else if (res?.data?.invitationEmailNote) {
        toast.info(
          res.data.invitationEmailNote ===
            "Candidate has no valid email address"
            ? "Interview scheduled, but the candidate has no valid email address."
            : "Interview scheduled, but the invitation email could not be sent.",
        );
      } else {
        toast.success("Interview scheduled");
      }
      setShowInterviewForm(false);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Interview could not be scheduled."));
    } finally {
      setSavingInterview(false);
    }
  };

  const cancelInterview = async (interview) => {
    setBusyId(`interview-${interview.id}`);
    try {
      await API.post(`/interviews/${interview.id}/cancel`);
      toast.success("Interview cancelled");
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not cancel interview"));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <LoadingState variant="spinner" label="Loading My Day…" />
      </Box>
    );
  }

  const interviewsToday = myDay?.interviewsToday || [];
  const tasksDueToday = myDay?.tasksDueToday || [];
  const overdueTasks = myDay?.overdueTasks || [];
  const positionsAwaitingCandidates = myDay?.positionsAwaitingCandidates || [];
  const candidatesAwaitingFeedback = myDay?.candidatesAwaitingFeedback || [];

  const nothingAtAll =
    interviewsToday.length === 0 &&
    tasksDueToday.length === 0 &&
    overdueTasks.length === 0 &&
    positionsAwaitingCandidates.length === 0 &&
    candidatesAwaitingFeedback.length === 0;

  // ── Phase 10 (Design Gap Audit item J): visual-parity additions ──────────
  // Subtitle: the prototype shows "{name} · N active roles · N interviews
  // scheduled". This page has no jobs/positions list loaded (that's
  // Positions.jsx's data), so "active roles" would have to be fabricated —
  // it's honestly omitted rather than invented. Only the recruiter's real
  // name (from the existing auth session) and the real interview count
  // (already loaded) are shown.
  // ── Feature A (My Day active roles) ──────────────────────────────────────
  const activeRolesCount = myJobs.filter(
    (j) =>
      (j.status || "ACTIVE") !== "ON_HOLD" &&
      (j.status || "ACTIVE") !== "CLOSED",
  ).length;

  const currentUserName = getUser()?.name;
  const followUpsDue = tasksDueToday.length + overdueTasks.length;

  const headerSubtitle = [
    currentUserName,
    myJobs.length > 0
      ? `${activeRolesCount} active role${activeRolesCount !== 1 ? "s" : ""}`
      : null,
    `${interviewsToday.length} interview${interviewsToday.length !== 1 ? "s" : ""} scheduled today`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <PageContainer sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY }}>
            My Day
          </Typography>
          {headerSubtitle && (
            <Typography
              sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}
            >
              {headerSubtitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <RefreshButton onClick={fetchAll} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventAvailableOutlinedIcon />}
            onClick={() => setShowInterviewForm(true)}
          >
            Schedule Interview
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openTaskForm({})}
          >
            New Task
          </Button>
        </Stack>
      </Stack>

      {/* ── Phase 10 (item J): 3-KPI row, matching the prototype ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            value={interviewsToday.length}
            label="Interviews today"
            color="#2563eb"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            value={followUpsDue}
            label="Follow-ups due"
            color="#d97706"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            value={positionsAwaitingCandidates.length}
            label="Roles with no profile"
            color="#dc2626"
          />
        </Grid>
      </Grid>

      {/* Everything below this point (empty state, or the Today's
          Interviews / Tasks / Needs Attention sections) lives in its own
          bounded, self-contained scroll region — matching Pipeline's exact
          pattern where the title/subtitle/filters stay fixed above and only
          the board content scrolls. The title, subtitle, and KPI row above
          are OUTSIDE this box, in normal page flow, so they stay visible
          the same way Pipeline's search/filter bar does. */}
      <Box
        sx={{
          maxHeight: "calc(100vh - 300px)",
          overflowX: "hidden",
          overflowY: "scroll",
          pr: 0.5,
          "&::-webkit-scrollbar": {
            width: 12,
            WebkitAppearance: "none",
            display: "block",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#e2e8f0",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#94a3b8",
            borderRadius: 8,
            border: "2px solid #e2e8f0",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#64748b",
          },
          // Firefox
          scrollbarWidth: "auto",
          scrollbarColor: "#94a3b8 #e2e8f0",
        }}
      >
        {nothingAtAll ? (
          <Paper sx={{ py: 2 }}>
            <EmptyState
              icon={
                <ChecklistOutlinedIcon sx={{ fontSize: 28, color: ACCENT }} />
              }
              title="Nothing on your plate today"
              description="Interviews, tasks, and items needing attention will show up here."
            />
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {/* Today's interviews */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <EventAvailableOutlinedIcon
                    sx={{ color: ACCENT, fontSize: 20 }}
                  />
                  <Typography sx={{ fontWeight: 700 }}>
                    Today's Interviews
                  </Typography>
                  <Chip
                    size="small"
                    label={interviewsToday.length}
                    variant="outlined"
                  />
                </Stack>
                {interviewsToday.length === 0 ? (
                  <Typography
                    sx={{ fontSize: 13, color: "text.secondary", py: 2 }}
                  >
                    No interviews scheduled today.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {interviewsToday.map((iv) => (
                      <Paper key={iv.id} variant="outlined" sx={{ p: 1.25 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Typography
                              sx={{ fontWeight: 700, fontSize: 13.5 }}
                            >
                              {candidateName(iv.candidateId)}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12, color: "text.secondary" }}
                            >
                              {new Date(iv.scheduledAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              {iv.round ? ` · ${iv.round}` : ""}
                              {iv.jobCode ? ` · ${iv.jobCode}` : ""}
                            </Typography>
                            {iv.meetingLink && (
                              <Chip
                                component="a"
                                href={iv.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                                size="small"
                                icon={
                                  <VideocamOutlinedIcon sx={{ fontSize: 13 }} />
                                }
                                label="Join"
                                sx={{
                                  mt: 0.5,
                                  height: 22,
                                  fontSize: 11,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </Box>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <StatusBadge
                              label={iv.status}
                              tone={
                                iv.status === "CANCELLED" ? "danger" : "info"
                              }
                            />
                            {iv.status !== "CANCELLED" && (
                              <IconButton
                                size="small"
                                disabled={busyId === `interview-${iv.id}`}
                                onClick={() => cancelInterview(iv)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Tasks */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <ChecklistOutlinedIcon sx={{ color: ACCENT, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 700 }}>Tasks</Typography>
                  <Chip
                    size="small"
                    label={tasksDueToday.length + overdueTasks.length}
                    variant="outlined"
                  />
                </Stack>
                {tasksDueToday.length === 0 && overdueTasks.length === 0 ? (
                  <Typography
                    sx={{ fontSize: 13, color: "text.secondary", py: 2 }}
                  >
                    No tasks due today or overdue.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {overdueTasks.map((t) => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        overdue
                        busy={busyId === `task-${t.id}`}
                        onComplete={() => completeTask(t)}
                        onSnooze={(days) => snoozeTask(t, days)}
                      />
                    ))}
                    {tasksDueToday.map((t) => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        busy={busyId === `task-${t.id}`}
                        onComplete={() => completeTask(t)}
                        onSnooze={(days) => snoozeTask(t, days)}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Needs attention */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <WarningAmberOutlinedIcon
                    sx={{ color: "#d97706", fontSize: 20 }}
                  />
                  <Typography sx={{ fontWeight: 700 }}>
                    Needs Attention
                  </Typography>
                </Stack>

                {positionsAwaitingCandidates.length === 0 &&
                candidatesAwaitingFeedback.length === 0 ? (
                  <Typography
                    sx={{ fontSize: 13, color: "text.secondary", py: 1 }}
                  >
                    Nothing needs attention right now.
                  </Typography>
                ) : (
                  <Grid container spacing={1.5}>
                    {positionsAwaitingCandidates.map((p) => (
                      <Grid item xs={12} sm={6} md={4} key={`pos-${p.jobCode}`}>
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            <WorkOutlineIcon
                              sx={{ fontSize: 14, color: ACCENT }}
                            />
                            <Typography
                              sx={{ fontSize: 12.5, color: "text.secondary" }}
                            >
                              No candidates yet
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{ fontWeight: 700, fontSize: 13.5, mt: 0.25 }}
                          >
                            {p.roleName || p.jobCode}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: "text.secondary" }}
                          >
                            {p.jobCode}{" "}
                            {p.companyName ? `· ${p.companyName}` : ""}
                          </Typography>
                          <Button
                            size="small"
                            sx={{ mt: 0.75, fontSize: 11.5 }}
                            onClick={() => openTaskForm({ jobCode: p.jobCode })}
                          >
                            + Follow-up task
                          </Button>
                        </Paper>
                      </Grid>
                    ))}
                    {candidatesAwaitingFeedback.map((c) => (
                      <Grid item xs={12} sm={6} md={4} key={`cand-${c.id}`}>
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            <WarningAmberOutlinedIcon
                              sx={{ fontSize: 14, color: "#d97706" }}
                            />
                            <Typography
                              sx={{ fontSize: 12.5, color: "text.secondary" }}
                            >
                              Awaiting feedback (3+ days)
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{ fontWeight: 700, fontSize: 13.5, mt: 0.25 }}
                          >
                            {c.name}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: "text.secondary" }}
                          >
                            {c.jobCode}
                          </Typography>
                          <Button
                            size="small"
                            sx={{ mt: 0.75, fontSize: 11.5 }}
                            onClick={() =>
                              openTaskForm({
                                candidateId: c.id,
                                jobCode: c.jobCode,
                              })
                            }
                          >
                            + Follow-up task
                          </Button>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* New Task dialog */}
      <Dialog
        open={showTaskForm}
        onClose={() => setShowTaskForm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New Task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Title"
              size="small"
              autoFocus
              value={taskForm.title}
              onChange={(e) =>
                setTaskForm((f) => ({ ...f, title: e.target.value }))
              }
            />
            <TextField
              label="Description"
              size="small"
              multiline
              minRows={2}
              value={taskForm.description}
              onChange={(e) =>
                setTaskForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Priority"
                size="small"
                fullWidth
                value={taskForm.priority}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, priority: e.target.value }))
                }
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </TextField>
              <TextField
                type="date"
                label="Due date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={taskForm.dueDate}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, dueDate: e.target.value }))
                }
              />
            </Stack>
            {taskForm.jobCode && (
              <Chip
                size="small"
                label={`Linked to ${taskForm.jobCode}`}
                onDelete={() =>
                  setTaskForm((f) => ({ ...f, jobCode: "", candidateId: null }))
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setShowTaskForm(false)} disabled={savingTask}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitTask}
            disabled={savingTask}
          >
            {savingTask ? "Saving…" : "Create Task"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Interview dialog */}
      <Dialog
        open={showInterviewForm}
        onClose={() => setShowInterviewForm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Schedule Interview</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete
              options={candidates}
              getOptionLabel={(c) =>
                `${c.firstName || ""} ${c.lastName || ""} (${c.jobCode || "—"})`
              }
              value={interviewForm.candidate}
              onChange={(_, val) =>
                setInterviewForm((f) => ({ ...f, candidate: val }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Candidate" size="small" />
              )}
            />
            <TextField
              type="datetime-local"
              label="Date & time"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={interviewForm.scheduledAt}
              onChange={(e) =>
                setInterviewForm((f) => ({ ...f, scheduledAt: e.target.value }))
              }
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Round"
                size="small"
                fullWidth
                placeholder="e.g. L1, L2, HR"
                value={interviewForm.round}
                onChange={(e) =>
                  setInterviewForm((f) => ({ ...f, round: e.target.value }))
                }
              />
              <TextField
                type="number"
                label="Duration (min)"
                size="small"
                fullWidth
                value={interviewForm.durationMinutes}
                onChange={(e) =>
                  setInterviewForm((f) => ({
                    ...f,
                    durationMinutes: e.target.value,
                  }))
                }
              />
            </Stack>
            <TextField
              label="Interviewer"
              size="small"
              value={interviewForm.interviewerName}
              onChange={(e) =>
                setInterviewForm((f) => ({
                  ...f,
                  interviewerName: e.target.value,
                }))
              }
            />
            <TextField
              label="Meeting link"
              size="small"
              value={interviewForm.meetingLink}
              onChange={(e) =>
                setInterviewForm((f) => ({ ...f, meetingLink: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setShowInterviewForm(false)}
            disabled={savingInterview}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitInterview}
            disabled={savingInterview}
          >
            {savingInterview ? "Scheduling…" : "Schedule"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}

function TaskRow({ task, overdue, busy, onComplete, onSnooze }) {
  const done = task.status === "DONE";
  const [snoozeAnchor, setSnoozeAnchor] = useState(null);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderColor: overdue ? "#fecaca" : undefined,
        opacity: busy ? 0.6 : 1,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 13.5,
              textDecoration: done ? "line-through" : "none",
            }}
          >
            {task.title}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mt: 0.25 }}
          >
            <StatusBadge
              label={task.priority}
              tone={PRIORITY_TONE[task.priority] || "neutral"}
            />
            {task.dueDate && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: overdue ? "#dc2626" : "text.secondary",
                }}
              >
                Due {task.dueDate}
              </Typography>
            )}
            {task.jobCode && (
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                · {task.jobCode}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.25}>
          {onSnooze && !done && (
            <>
              <Tooltip title="Snooze">
                <span>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={(e) => setSnoozeAnchor(e.currentTarget)}
                  >
                    <SnoozeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={snoozeAnchor}
                open={!!snoozeAnchor}
                onClose={() => setSnoozeAnchor(null)}
              >
                {[1, 3, 7].map((d) => (
                  <MenuItem
                    key={d}
                    onClick={() => {
                      onSnooze(d);
                      setSnoozeAnchor(null);
                    }}
                  >
                    Snooze {d} day{d !== 1 ? "s" : ""}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Tooltip title={done ? "Completed" : "Mark complete"}>
            <span>
              <IconButton
                size="small"
                disabled={busy || done}
                onClick={onComplete}
              >
                <CheckCircleOutlineIcon
                  fontSize="small"
                  sx={{ color: done ? "#16a34a" : undefined }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}
